// Optional panels download their styles without holding up the storefront's first
// paint. Opening a panel still waits for its complete stylesheet group.
const pendingStyles = new WeakMap();
const LOAD_TIMEOUT = 8000;
let retryCount = 0;

function readyStylesheet(link) {
  const previous = pendingStyles.get(link);
  if (previous && !previous.failed) return previous.ready;
  if (previous?.failed && (link.dataset.themeStyleState === 'error' || !link.sheet)) {
    // A later explicit open action can retry a failed request. Replace in place
    // so print/colour overrides keep their original cascade order.
    const retry = link.cloneNode(true);
    const retryUrl = new URL(link.href);
    // Chrome can reuse a failed resource for the same URL. Keep Shopify's
    // version token and request a fresh resource only after an explicit retry.
    retryUrl.searchParams.set('theme_style_retry', String(++retryCount));
    retry.href = retryUrl.href;
    retry.media = 'print';
    delete retry.dataset.themeStyleState;
    link.replaceWith(retry);
    return readyStylesheet(retry);
  }
  const pending = {failed: false, ready: null};
  pendingStyles.set(link, pending);
  pending.ready = new Promise((resolve, reject) => {
    let timeout;
    const cleanup = () => {
      clearTimeout(timeout);
      link.removeEventListener('load', loaded);
      link.removeEventListener('error', failed);
    };
    const loaded = () => {
      cleanup();
      link.media = 'all';
      link.dataset.themeStyleState = 'loaded';
      // Let the newly enabled rules apply before measuring or focusing a panel.
      requestAnimationFrame(resolve);
    };
    const failed = () => {
      cleanup();
      pending.failed = true;
      link.dataset.themeStyleState = 'error';
      reject(new Error(`Unable to load optional stylesheet: ${link.href}`));
    };

    if (link.dataset.themeStyleState === 'error') {
      failed();
    } else if (link.sheet) {
      loaded();
    } else {
      link.addEventListener('load', loaded, {once: true});
      link.addEventListener('error', failed, {once: true});
      timeout = setTimeout(failed, LOAD_TIMEOUT);
    }
  });
  return pending.ready;
}

export function ensureDeferredStyles(...groups) {
  const styles = [...document.querySelectorAll('link[data-theme-style-group]')].filter(link =>
    link.dataset.themeStyleGroup.split(/\s+/).some(group => groups.includes(group)),
  );
  return Promise.all(styles.map(readyStylesheet));
}

function releaseSwatches() {
  document.documentElement.removeAttribute('data-theme-swatches-pending');
}

function prepareSwatches() {
  if (!document.documentElement.hasAttribute('data-theme-swatches-pending')) return;
  // Initial swatches keep normal blocking CSS. This only guards swatches added
  // later on pages that initially have none, including search and drawer cards.
  ensureDeferredStyles('swatches').then(releaseSwatches, releaseSwatches);
}

if (typeof document !== 'undefined' && document.documentElement) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prepareSwatches, {once: true});
  } else {
    prepareSwatches();
  }
}
